// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, forkJoin, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import { DeleteTimeGroupRequest, } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeleteTimeGroupRpc = (request: DeleteTimeGroupRequest) => Observable<Empty>

export const deleteTimeGroups = (deleteTimeGroupRpc: DeleteTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimeGroups.match),
      mergeMap(({ payload: { requests, selectedTimeRowsIds }}) =>
        forkJoin(requests.map(request => deleteTimeGroupRpc(request)))
          .pipe(
            mergeMap(_ => concat(
              of(TimelineSlice.deleteTimeGroups(requests.map(r => r.getTimeGroupId()))),
              of(TimelineSlice.addActiveTimeRowIds({ timeRowIds: selectedTimeRowsIds })),
            )),
            catchError(handleError),
          )
      ),
    )

const deleteTimeGroupRpc = (request: DeleteTimeGroupRequest): Observable<Empty> =>
  grpc(() => timelineClient.deleteTimeGroup(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_delete_time_group_failed_precondition",
            message: "Failed to delete time group. Time row(s) or time group may be out of sync."
          }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_time_group_failed",
      message: "Failed to delete group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export default deleteTimeGroups(deleteTimeGroupRpc)
